import React, {useState, useEffect, useMemo, useRef} from 'react'
import {
    Heading,
    Stack,
    Divider,
    Button,
    Text,
    useBreakpointValue,
    Image,
    useStyleConfig,
    Box
} from '@salesforce/retail-react-app/app/components/shared/ui'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'
import ColorRefinements from './color-refinements'
import SizeRefinements from './size-refinements'
import RadioRefinements from './radio-refinements'
import CheckboxRefinements from './checkbox-refinements'
import {isServer} from '@salesforce/retail-react-app/app/utils/utils'
import {FILTER_ACCORDION_SATE} from '@salesforce/retail-react-app/app/constants'
import {getUrl} from '../../../utils/cloudinary-utils'
import {BM_FILTER_MAPPING} from '../../../constants'

export const componentMap = {
    c_stoneColor: ColorRefinements,
    c_metal: ColorRefinements,
    metal: ColorRefinements,
    Metal: ColorRefinements,
    'SKU Color': ColorRefinements,
    Color: ColorRefinements,
    c_colorGeneral: ColorRefinements,
    'Ring Size': SizeRefinements,
    c_size: SizeRefinements,
    price: RadioRefinements,
    Price: RadioRefinements
}

const DropdownRefinements = ({
    itemsBefore,
    excludedFilters = [],
    filters = [],
    toggleFilter,
    selectedFilters,
    isMenu = false,
    onOpen
}) => {
    const accordionRef = useRef(null)
    const intl = useIntl()
    const styles = useStyleConfig('accordion')
    const productListStyles = useStyleConfig('ProductListStyles')
    const [selectedAttribute, setSelectedAttribute] = useState(null)
    const isMobile = useBreakpointValue({base: true, md: false})

    useEffect(() => {
        if (!isMenu) {
            const handleClickOutside = (event) => {
                if (
                    accordionRef.current &&
                    !accordionRef.current.contains(event.target) &&
                    !event.target.classList.contains('accordion-action') &&
                    !event.target.classList.contains('unselect-refinement') &&
                    !event.target.closest('.custom-accordion__panel')
                ) {
                    setSelectedAttribute(null)
                }
            }
            const onScroll = () => setSelectedAttribute(null)

            document.addEventListener('click', handleClickOutside)
            document.addEventListener('scroll', onScroll)
            return () => {
                document.removeEventListener('click', handleClickOutside)
                document.removeEventListener('scroll', onScroll)
            }
        }
    }, [])

    const visibleFilters = useMemo(
        () =>
            excludedFilters?.length
                ? filters.filter(({attributeId}) => !excludedFilters.includes(attributeId))
                : filters,
        [excludedFilters, filters]
    )

    const filtersIndexes = useMemo(() => {
        const indexes = visibleFilters?.map((filter, idx) => idx)

        if (isServer) {
            return indexes
        }

        const filterAccordionState = window.localStorage.getItem(FILTER_ACCORDION_SATE)
        const savedExpandedAccordionIndexes =
            filterAccordionState && JSON.parse(filterAccordionState)

        if (!savedExpandedAccordionIndexes) {
            return indexes
        }

        return visibleFilters
            ?.map((filter, index) =>
                savedExpandedAccordionIndexes.includes(filter.attributeId) ? index : undefined
            )
            .filter((index) => index !== undefined)
    }, [visibleFilters])

    return (
        <Stack
            spacing={8}
            className="refinements-wrapper"
            display="block"
            position="relative"
            width="100%"
        >
            {filtersIndexes && (
                <Box
                    ref={accordionRef}
                    data-active-attribute={selectedAttribute}
                    display={{base: 'contents', md: 'inline-flex'}}
                    flexWrap="wrap"
                >
                    {itemsBefore}
                    {visibleFilters?.map((filter, idx) => {
                        const Values = componentMap[filter.attributeId] || CheckboxRefinements
                        let selectedFiltersArray = selectedFilters?.[filter.attributeId] ?? []
                        const validValues = filter?.values?.map((v) => v.value)
                        if (!Array.isArray(selectedFiltersArray)) {
                            selectedFiltersArray = [selectedFiltersArray]
                        } else {
                            selectedFiltersArray = selectedFiltersArray?.filter((val) =>
                                validValues.includes(val)
                            )
                        }

                        if (filter.values) {
                            const allZero = filter.values.every((item) => item.hitCount === 0)

                            if (allZero) {
                                return null
                            }

                            const isExpanded = filter.attributeId === selectedAttribute
                            return (
                                <Stack
                                    key={filter.attributeId}
                                    divider={<Divider />}
                                    margin={0}
                                    alignContent="center"
                                    display={{
                                        lg: !isMenu && idx > 3 ? 'none' : 'block',
                                        xl: !isMenu && idx > 5 ? 'none' : 'block'
                                    }}
                                >
                                    <Box border="none" position="relative">
                                        <Box
                                            padding="0 15px 0 0"
                                            alignItems="baseline"
                                            aria-label={filter.attributeId}
                                            className="custom-accordion__button"
                                            data-attribute={selectedAttribute}
                                            display="flex"
                                            margin="0"
                                            onClick={(event) => {
                                                const isPanelActive = event.target.closest(
                                                    `div[data-attribute="${filter.attributeId}"]`
                                                )
                                                setSelectedAttribute(
                                                    isPanelActive ? null : filter.attributeId
                                                )
                                            }}
                                        >
                                            <Heading
                                                as="p"
                                                flex="1"
                                                fontWeight="400"
                                                cursor="pointer"
                                                fontSize={{
                                                    md: '18px',
                                                    base: '16px'
                                                }}
                                                textAlign="left"
                                                fontFamily={{base: 'body', md: 'heading'}}
                                                color="black"
                                                minHeight="29px"
                                                whiteSpace="nowrap"
                                                display="flex"
                                                data-label={filter.label}
                                                alignItems="center"
                                            >
                                                {BM_FILTER_MAPPING?.[filter.label] || filter.label}
                                                {selectedFiltersArray?.length ? (
                                                    <Box
                                                        display="block"
                                                        marginLeft="15px"
                                                        backgroundColor="gray.50"
                                                        borderRadius="50%"
                                                        fontSize="13px"
                                                        fontFamily="body"
                                                        alignContent="center"
                                                        width="29px"
                                                        height="29px"
                                                        textAlign="center"
                                                    >
                                                        {selectedFiltersArray?.length}
                                                    </Box>
                                                ) : (
                                                    <></>
                                                )}
                                            </Heading>
                                            {/* Icon Container */}
                                            <div
                                                style={{
                                                    transform: isExpanded
                                                        ? 'rotate(180deg)'
                                                        : 'rotate(0deg)',
                                                    display: 'flex',
                                                    margin: '0 15px',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    width: '13px',
                                                    height: '13px'
                                                }}
                                            >
                                                {isExpanded ? (
                                                    <Image
                                                        alt="Expand Icon"
                                                        cursor="pointer"
                                                        src={getUrl(
                                                            'UIImages/images/down-arrow-new.svg'
                                                        )}
                                                        className="accordion-action"
                                                        {...productListStyles.body.refinements
                                                            .arrowImage}
                                                    />
                                                ) : (
                                                    <Image
                                                        alt="Expand Icon"
                                                        cursor="pointer"
                                                        src={getUrl(
                                                            'UIImages/images/down-arrow-new.svg'
                                                        )}
                                                        className="accordion-action"
                                                        {...productListStyles.body.refinements
                                                            .arrowImage}
                                                    />
                                                )}
                                            </div>
                                        </Box>
                                        {/* Accordion Panel with transition */}
                                        <Box
                                            className="custom-accordion__panel"
                                            position={{
                                                base: 'unset',
                                                md: 'absolute'
                                            }}
                                            fontSize="16px"
                                            fontWeight="300"
                                            sx={{
                                                '& .chakra-checkbox__control svg': {
                                                    stroke: 'purple.100 !important'
                                                }
                                            }}
                                            borderRadius="4px"
                                            border={{base: 0, md: '1px solid'}}
                                            borderColor="gray.500 !important"
                                            padding={{
                                                base: '0 0 10px 0rem',
                                                md:
                                                    filter.attributeId === 'c_size' ||
                                                    filter.attributeId === 'Size' ||
                                                    filter.attributeId === 'c_RingSize' ||
                                                    filter.attributeId === 'Ring Size'
                                                        ? '0 10px 10px 10px'
                                                        : '10px'
                                            }}
                                            pointerEvents={isExpanded ? 'auto' : 'none'}
                                            style={{
                                                transition: 'height 0.3s ease, opacity 0.3s ease',
                                                height: isExpanded ? 'auto' : '0',
                                                opacity: isExpanded ? '1' : '0',
                                                overflow: 'hidden'
                                            }}
                                            paddingTop="0"
                                            paddingBottom="0"
                                            left="0"
                                            right="0"
                                            top="167%"
                                            zIndex="5"
                                            minWidth={
                                                filter.attributeId === 'c_size' ||
                                                filter.attributeId === 'Size' ||
                                                filter.attributeId === 'c_RingSize' ||
                                                filter.attributeId === 'Ring Size'
                                                    ? '372px'
                                                    : '375px'
                                            }
                                            paddingLeft="0"
                                            background="white"
                                            {...styles.pannel}
                                            width="max-content"
                                        >
                                            {isExpanded && (
                                                <Values
                                                    selectedFilters={selectedFiltersArray}
                                                    filter={filter}
                                                    isMenu={isMenu}
                                                    toggleFilter={toggleFilter}
                                                />
                                            )}
                                        </Box>
                                    </Box>
                                </Stack>
                            )
                        } else {
                            return null
                        }
                    })}
                </Box>
            )}
            {!isMenu && !isMobile && visibleFilters?.length && (
                <Button
                    onClick={() => onOpen(true)}
                    variant="link"
                    width="fit-content"
                    color="black"
                    aria-label={intl.formatMessage({
                        id: 'product_list.button.all_filters',
                        defaultMessage: 'All Filters'
                    })}
                    fontFamily="heading"
                    _hover={{color: 'black'}}
                    transition="color 0.3s ease"
                    fontSize="18px"
                >
                    <Text className="all-filters">
                        <FormattedMessage
                            defaultMessage="All Filters"
                            id="product_list.button.all_filters"
                        />
                    </Text>
                    <Image
                        alt="Filter Icon"
                        src={getUrl('UIImages/images/filter-icon.svg')}
                        width="18px"
                        height="18px"
                        marginLeft="0.5rem"
                    />
                </Button>
            )}
        </Stack>
    )
}

DropdownRefinements.propTypes = {
    itemsBefore: PropTypes.arrayOf(PropTypes.element),
    filters: PropTypes.array,
    excludedFilters: PropTypes.arrayOf(PropTypes.string),
    toggleFilter: PropTypes.func,
    selectedFilters: PropTypes.object,
    isLoading: PropTypes.bool,
    isMenu: PropTypes.bool,
    onOpen: PropTypes.func
}

export default React.memo(DropdownRefinements)
